import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { orderBy, startCase } from "lodash";
import { ArrowDownIcon, ArrowUpIcon } from "@heroicons/react/20/solid";
import { useTranslation } from "react-i18next";

import Loading from "@components/shared/Loading";
import LoadFailed, { showsOwnFailure } from "@components/zenith/ui/LoadFailed";
import Tag from "@components/shared/Tag";
import Table from "@components/zenith/ui/Table";
import { useAppContext } from "@context/AppContext";
import { useAllEventTransactions } from "@hooks/query/useAllEventTransactions";
import { formatVolume } from "@utils/formatVolume";
import { formatDateTime } from "@utils/formatDateTime";
import { formatSchemeDay } from "@utils/schemeDay";

/** Transactions shown per "See more". */
const PAGE_SIZE = 20;

const TransactionHistory: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const { id: level0ResourceId = "" } = useParams();
  const { currentAccountingPeriod, isBetweenPeriods } = useAppContext();
  const [shown, setShown] = useState(PAGE_SIZE);

  // The API scopes customers to their own wallet, and to the period asked for.
  // ponytail: loads the zone's whole history for the period and pages in the
  // browser, since the API can't drop zero-volume or overuse-adjustment
  // events; page on the server once it can.
  const { data, isPreviousData, isError } = useAllEventTransactions({
    params: {
      level0ResourceId,
      accountingPeriodId: currentAccountingPeriod.id,
      includeParentId: true,
      limit: -1,
    },
    // The period's id is empty until it loads, and between periods.
    enabled: Boolean(level0ResourceId && currentAccountingPeriod.id),
    refetchOnWindowFocus: false,
    ...showsOwnFailure,
  });

  if (isError) return <LoadFailed />;
  // Until this period's list arrives. A disabled query never loads, and the
  // last period's list stands in for a new one's, so neither isLoading nor
  // data alone says it has.
  if (!isBetweenPeriods && (!data || isPreviousData)) {
    return (
      <div className="pt-20">
        <Loading />
      </div>
    );
  }

  // Between periods none list, whatever the cache still holds.
  const transactions = isBetweenPeriods
    ? []
    : orderBy(
        (data.eventTransactions ?? []).filter(
          (event: any) =>
            Number(event.transactionAmount) !== 0 &&
            event.eventType?.name !== "OveruseAdjustmentTokenTransfer",
        ),
        ["effectiveAt", "createdAt"],
        ["desc", "desc"],
      );

  return (
    <div className="space-y-3">
      <Table
        aria-label={
          t("zenith.zone_accounts.tabs.transaction_history") as string
        }
        rowKey={(event: any) => event.id}
        rows={transactions.slice(0, shown)}
        empty={
          isBetweenPeriods
            ? t("zenith.dashboard.no_active_period")
            : t("common.no_transaction_data")
        }
        columns={[
          {
            key: "type",
            label: t("audit_trail.type"),
            render: (event: any) => startCase(event.eventType?.name),
          },
          {
            key: "description",
            label: t("common.description"),
            render: (event: any) => event.calculation ?? event.description,
          },
          {
            key: "volume",
            label: t("audit_trail.volume"),
            render: (event: any) => (
              <VolumeTag volume={+event.transactionAmount} />
            ),
          },
          {
            key: "effectiveAt",
            label: t("common.effective_date"),
            render: (event: any) =>
              event.effectiveAt ? formatSchemeDay(event.effectiveAt) : "-",
          },
          {
            key: "createdBy",
            label: t("common.created_by"),
            render: (event: any) => <CreatedBy user={event.createdByUser} />,
          },
          {
            key: "createdAt",
            label: t("audit_trail.timestamp"),
            render: (event: any) => (
              <span className="whitespace-nowrap">
                {formatDateTime(new Date(event.createdAt))}
              </span>
            ),
          },
        ]}
      />
      {transactions.length > shown ? (
        <button
          type="button"
          onClick={() => setShown(count => count + PAGE_SIZE)}
          className="text-sm font-semibold text-primary-1 hover:underline"
        >
          {t("common.see_more")}
        </button>
      ) : null}
    </div>
  );
};

/** A signed volume in litres: green with an up arrow in, red with a down arrow out. */
const VolumeTag: React.FunctionComponent<{ volume: number }> = ({ volume }) => (
  <Tag
    status={volume > 0 ? "success" : "error"}
    className="inline-flex items-center gap-1 whitespace-nowrap py-1 pl-2 pr-3"
  >
    {volume > 0 ? (
      <ArrowUpIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
    ) : (
      <ArrowDownIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
    )}
    {formatVolume(volume)}
  </Tag>
);

/** Who made a record: an admin, a user, or the system when no user did. */
export const CreatedBy: React.FunctionComponent<{
  user?: { isAdmin: boolean } | null;
}> = ({ user }) => {
  const { t } = useTranslation();

  return (
    <>
      {user
        ? user.isAdmin
          ? t("common.admin")
          : t("common.user")
        : t("common.system")}
    </>
  );
};

export default TransactionHistory;
